import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, 
  ChevronDown, 
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Info,
  Users,
  Shield,
  Gamepad2,
  Download
} from 'lucide-react';
import SEO from '../components/SEO';

// Tip tanımlamaları
interface StepContent {
  type: 'step';
  title: string;
  description: string;
  details: string[];
}

interface CommandContent {
  type: 'command';
  command: string;
  description: string;
  category: string;
}

interface JobContent {
  type: 'job';
  title: string;
  description: string;
  requirements: string[];
  salary: string;
  benefits: string[];
}

interface RuleContent {
  type: 'rule';
  title: string;
  severity: string;
  rules: string[];
}

interface IssueContent {
  type: 'issue';
  title: string;
  description: string;
  solutions: string[];
}

type ContentItem = StepContent | CommandContent | JobContent | RuleContent | IssueContent;

interface Section {
  id: string;
  title: string;
  icon: any;
  content: ContentItem[];
}

const ServerGuide: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['getting-started']);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const sections: Section[] = [
    {
      id: 'getting-started',
      title: 'Başlangıç Rehberi',
      icon: BookOpen,
      content: [
        {
          type: 'step',
          title: '1. FiveM İndirme ve Kurulum',
          description: 'FiveM client\'ını resmi web sitesinden indirin ve kurun.',
          details: [
            'FiveM web sitesine gidin (fivem.net)',
            'Download butonuna tıklayın',
            'İndirilen dosyayı çalıştırın',
            'Kurulum tamamlandıktan sonra FiveM\'i başlatın'
          ]
        },
        {
          type: 'step',
          title: '2. Sunucuya Bağlanma',
          description: 'Sunucu IP adresini kullanarak bağlanın.',
          details: [
            'FiveM\'de F8 tuşuna basın',
            'connect [SUNUCU_IP] yazın',
            'Enter tuşuna basın',
            'Sunucuya bağlanmayı bekleyin'
          ]
        },
        {
          type: 'step',
          title: '3. İlk Giriş',
          description: 'İlk girişinizde karakter oluşturun.',
          details: [
            'Karakter adınızı belirleyin',
            'Karakter görünümünü özelleştirin',
            'Başlangıç mesleğini seçin',
            'Oyun dünyasına giriş yapın'
          ]
        }
      ]
    },
    {
      id: 'commands',
      title: 'Temel Komutlar',
      icon: Gamepad2,
      content: [
        {
          type: 'command',
          command: '/help',
          description: 'Mevcut komutları listeler',
          category: 'Genel'
        },
        {
          type: 'command',
          command: '/me [eylem]',
          description: 'Karakterinizin yaptığı eylemi gösterir',
          category: 'Roleplay'
        },
        {
          type: 'command',
          command: '/do [durum]',
          description: 'Çevrenizdeki durumu belirtir',
          category: 'Roleplay'
        },
        {
          type: 'command',
          command: '/ooc [mesaj]',
          description: 'Out of character mesaj gönderir',
          category: 'İletişim'
        },
        {
          type: 'command',
          command: '/report [şikayet]',
          description: 'Yöneticilere şikayet bildirir',
          category: 'Destek'
        }
      ]
    },
    {
      id: 'jobs',
      title: 'Meslekler ve İşler',
      icon: Users,
      content: [
        {
          type: 'job',
          title: 'Polis Mesleği',
          description: 'Şehir güvenliğini sağlayan resmi meslek',
          requirements: ['18 yaş üstü', 'Temiz sicil', 'Mülakat geçmek'],
          salary: '5000-15000$',
          benefits: ['Resmi araç kullanımı', 'Silah taşıma izni', 'Özel ekipman']
        },
        {
          type: 'job',
          title: 'Doktor Mesleği',
          description: 'Sağlık hizmetleri sunan meslek',
          requirements: ['Tıp eğitimi', 'Sertifika', 'Deneyim'],
          salary: '4000-12000$',
          benefits: ['Hastane erişimi', 'Tıbbi ekipman', 'Özel araçlar']
        },
        {
          type: 'job',
          title: 'Mekanik Mesleği',
          description: 'Araç tamir ve bakım hizmetleri',
          requirements: ['Teknik bilgi', 'Araç tamir deneyimi'],
          salary: '3000-10000$',
          benefits: ['Garaj erişimi', 'Araç parçaları', 'Özel araçlar']
        }
      ]
    },
    {
      id: 'rules',
      title: 'Sunucu Kuralları',
      icon: Shield,
      content: [
        {
          type: 'rule',
          title: 'Genel Kurallar',
          severity: 'high',
          rules: [
            'Oyunculara saygılı olun',
            'Hakaret ve küfür yasaktır',
            'Spam ve flood yapmayın',
            'Hile kullanımı kesinlikle yasaktır'
          ]
        },
        {
          type: 'rule',
          title: 'Roleplay Kuralları',
          severity: 'medium',
          rules: [
            'Karakterinizi tutarlı oynayın',
            'Metagaming yapmayın',
            'Powergaming yapmayın',
            'Fail RP yapmayın'
          ]
        },
        {
          type: 'rule',
          title: 'İletişim Kuralları',
          severity: 'medium',
          rules: [
            'Sadece Türkçe konuşun',
            'Ses kalitesi iyi olmalı',
            'Gereksiz ses yapmayın',
            'Müzik çalmayın'
          ]
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Sorun Giderme',
      icon: AlertCircle,
      content: [
        {
          type: 'issue',
          title: 'Bağlantı Sorunları',
          description: 'Sunucuya bağlanamıyorsanız',
          solutions: [
            'İnternet bağlantınızı kontrol edin',
            'Firewall ayarlarınızı kontrol edin',
            'FiveM\'i yeniden başlatın',
            'Sunucu durumunu kontrol edin'
          ]
        },
        {
          type: 'issue',
          title: 'Performans Sorunları',
          description: 'FPS düşüklüğü yaşıyorsanız',
          solutions: [
            'Grafik ayarlarını düşürün',
            'Gereksiz programları kapatın',
            'Drivers\'ları güncelleyin',
            'RAM kullanımını kontrol edin'
          ]
        },
        {
          type: 'issue',
          title: 'Ses Sorunları',
          description: 'Ses ile ilgili problemler',
          solutions: [
            'Mikrofon ayarlarını kontrol edin',
            'Ses sürücülerini güncelleyin',
            'FiveM ses ayarlarını kontrol edin',
            'Discord ayarlarını kontrol edin'
          ]
        }
      ]
    }
  ];

  return (
    <>
      <SEO
        title="Sunucu Rehberi - FiveM | Aedon Community"
        description="FiveM sunucumuzda oyun oynamak için ihtiyacınız olan tüm bilgiler. Başlangıç rehberi, komutlar, meslekler, kurallar ve sorun giderme."
        keywords="FiveM, sunucu rehberi, oyun, oyuncu, komutlar, meslekler, kurallar, sorun giderme, Aedon Community, FiveM rehber"
        url="/guide"
      />
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
            Sunucu Rehberi
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Sunucumuzda oyun oynamak için ihtiyacınız olan tüm bilgiler
          </p>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12"
        >
          <div className="card text-center">
            <Download className="w-8 h-8 text-primary-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">FiveM İndir</h3>
            <p className="text-gray-400 text-sm mb-4">Resmi FiveM client'ını indirin</p>
            <a href="https://fivem.net" target="_blank" rel="noopener noreferrer" className="btn-primary text-sm inline-flex items-center justify-center">
              <Download className="w-4 h-4 mr-2" />
              İndir
            </a>
          </div>
          <div className="card text-center">
            <Users className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">Discord</h3>
            <p className="text-gray-400 text-sm mb-4">Community sunucusuna katılın</p>
            <a href="https://discord.gg/aedonv" target="_blank" rel="noopener noreferrer" className="btn-primary text-sm inline-flex items-center justify-center">
              <Users className="w-4 h-4 mr-2" />
              Katıl
            </a>
          </div>
          <div className="card text-center">
            <Shield className="w-8 h-8 text-purple-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">Kurallar</h3>
            <p className="text-gray-400 text-sm mb-4">Sunucu kurallarını okuyun</p>
            <a href="/rules" className="btn-primary text-sm inline-flex items-center justify-center">
              <Shield className="w-4 h-4 mr-2" />
              Oku
            </a>
          </div>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => {
            const Icon = section.icon;
            const isExpanded = expandedSections.includes(section.id);
            
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-6 h-6 text-primary-400" />
                    <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 space-y-6"
                  >
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex} className="border-l-2 border-primary-500 pl-6">
                        {item.type === 'step' && (
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                            <p className="text-gray-400 mb-3">{item.description}</p>
                            <ul className="space-y-2">
                              {item.details.map((detail, detailIndex) => (
                                <li key={detailIndex} className="flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-300 text-sm">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {item.type === 'command' && (
                          <div className="bg-dark-700 p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <code className="text-primary-400 font-mono">{item.command}</code>
                              <span className="text-xs text-gray-400 bg-dark-600 px-2 py-1 rounded">
                                {item.category}
                              </span>
                            </div>
                            <p className="text-gray-300 text-sm">{item.description}</p>
                          </div>
                        )}

                        {item.type === 'job' && (
                          <div className="bg-dark-700 p-4 rounded-lg">
                            <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                            <p className="text-gray-400 mb-3">{item.description}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold text-white mb-2">Gereksinimler:</h4>
                                <ul className="space-y-1">
                                  {item.requirements.map((req, reqIndex) => (
                                    <li key={reqIndex} className="text-gray-300 text-sm flex items-center space-x-2">
                                      <Info className="w-3 h-3 text-blue-400" />
                                      <span>{req}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-white mb-2">Avantajlar:</h4>
                                <ul className="space-y-1">
                                  {item.benefits.map((benefit, benefitIndex) => (
                                    <li key={benefitIndex} className="text-gray-300 text-sm flex items-center space-x-2">
                                      <CheckCircle className="w-3 h-3 text-green-400" />
                                      <span>{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            <div className="mt-3 text-sm text-gray-400">
                              Maaş: <span className="text-primary-400">{item.salary}</span>
                            </div>
                          </div>
                        )}

                        {item.type === 'rule' && (
                          <div className="bg-dark-700 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-3">
                              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                              <span className={`text-xs px-2 py-1 rounded ${
                                item.severity === 'high' 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {item.severity === 'high' ? 'Kritik' : 'Önemli'}
                              </span>
                            </div>
                            <ul className="space-y-2">
                              {item.rules.map((rule, ruleIndex) => (
                                <li key={ruleIndex} className="text-gray-300 text-sm flex items-start space-x-2">
                                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                                  <span>{rule}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {item.type === 'issue' && (
                          <div className="bg-dark-700 p-4 rounded-lg">
                            <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                            <p className="text-gray-400 mb-3">{item.description}</p>
                            <h4 className="text-sm font-semibold text-white mb-2">Çözümler:</h4>
                            <ul className="space-y-2">
                              {item.solutions.map((solution, solutionIndex) => (
                                <li key={solutionIndex} className="text-gray-300 text-sm flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                  <span>{solution}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
};

export default ServerGuide; 